import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { formatUsdFromCents, formatUnixDate } from '../analytics/formatAnalytics';
import {
  getWorkspaceDetail,
  getWorkspaceSettings,
  patchWorkspaceInChannelReminders,
  patchWorkspaceReminderCadence,
  postComplimentaryGrant,
} from '../api/client';
import { BroadcastMentionTrackingToggle } from '../components/BroadcastMentionTrackingToggle';
import type {
  ClientDetailResponse,
  ClientIdentity,
  ClientPayments,
  InChannelRemindersMode,
  PaymentHistoryRowType,
  WorkspaceSettingsResponse,
} from '../api/types';
import { CadenceDefaultRow, InChannelDefaultRow } from '../settings/TrackingDefaultRows';
import { PAYMENT_TYPE_LABELS } from './analytics/paymentsTypeLabels';

type ClientPaymentHistoryItem = Omit<
  NonNullable<ClientPayments['paymentHistory']>[number],
  'type'
> & {
  type?: PaymentHistoryRowType;
};

const COMPLIMENTARY_GRANT_PACK_LABEL = 'Complimentary grant';
export const PUBLIC_INSTALL_GRANT_PACK_LABEL = 'Public install track grant';
const PUBLIC_INSTALL_GRANT_TYPE_LABEL = 'Public install grant';
const COMPLIMENTARY_GRANT_PAYMENT_ID_PREFIX = 'grant:complimentary_grant:';
const INSTALL_GRANT_PAYMENT_ID_PREFIX = 'grant:install_grant:';

type GrantDiscriminationFields = Pick<
  ClientPaymentHistoryItem,
  'type' | 'grantedByEmail' | 'paymentId' | 'pack' | 'description'
>;

/** Complimentary grants: legacy type, admin grantedByEmail, or complimentary paymentId prefix. */
export function isComplimentaryGrantRow(p: GrantDiscriminationFields): boolean {
  if (p.type === 'complimentary') return true;
  if (p.grantedByEmail?.trim()) return true;
  const paymentId = p.paymentId?.trim();
  return Boolean(paymentId?.startsWith(COMPLIMENTARY_GRANT_PAYMENT_ID_PREFIX));
}

/** Public install grant rows — not complimentary per TRD §8 / edge case 6. */
export function isInstallGrantRow(p: GrantDiscriminationFields): boolean {
  if (isComplimentaryGrantRow(p)) return false;
  const paymentId = p.paymentId?.trim();
  if (paymentId?.startsWith(INSTALL_GRANT_PAYMENT_ID_PREFIX)) return true;
  const packLabel = p.pack?.trim() || p.description?.trim();
  if (packLabel === PUBLIC_INSTALL_GRANT_PACK_LABEL) return true;
  return p.type === 'grant';
}

export function paymentHistoryTypeLabel(row: GrantDiscriminationFields): string {
  if (isInstallGrantRow(row)) {
    return PUBLIC_INSTALL_GRANT_TYPE_LABEL;
  }
  if (isComplimentaryGrantRow(row)) {
    return PAYMENT_TYPE_LABELS.complimentary;
  }
  return PAYMENT_TYPE_LABELS[(row.type as PaymentHistoryRowType) ?? 'unknown'];
}

export function parseTracksToGrantInput(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed <= 0) return null;
  return parsed;
}

type WorkspaceDetailTab = 'overview' | 'settings';

export function ClientDetailPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: WorkspaceDetailTab = searchParams.get('tab') === 'settings' ? 'settings' : 'overview';
  const [data, setData] = useState<ClientDetailResponse | null>(null);
  const [settings, setSettings] = useState<WorkspaceSettingsResponse | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tracksToGrantInput, setTracksToGrantInput] = useState('');
  const [grantValidationError, setGrantValidationError] = useState<string | null>(null);
  const [grantSubmitError, setGrantSubmitError] = useState<string | null>(null);
  const [grantSuccessMessage, setGrantSuccessMessage] = useState<string | null>(null);
  const [grantSubmitting, setGrantSubmitting] = useState(false);
  const [showGrantConfirm, setShowGrantConfirm] = useState(false);
  const [pendingTracksToGrant, setPendingTracksToGrant] = useState<number | null>(null);

  const loadDetail = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!teamId) return;
      const silent = options?.silent ?? false;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const detail = await getWorkspaceDetail(teamId);
        setData(detail);
      } catch (e) {
        if (!silent) {
          setError(e instanceof Error ? e.message : 'Failed to load client');
          setData(null);
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [teamId]
  );

  const loadSettings = useCallback(async () => {
    if (!teamId) return;
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const next = await getWorkspaceSettings(teamId);
      setSettings(next);
    } catch (e) {
      setSettingsError(e instanceof Error ? e.message : 'Failed to load workspace settings');
      setSettings(null);
    } finally {
      setSettingsLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    if (tab === 'settings') {
      void loadSettings();
    }
  }, [tab, loadSettings]);

  function selectTab(next: WorkspaceDetailTab) {
    if (next === 'settings') {
      setSearchParams({ tab: 'settings' }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }

  async function handleSaveCadence(minutes: number) {
    if (!teamId) return;
    const next = await patchWorkspaceReminderCadence(teamId, minutes);
    setSettings((prev) => (prev ? { ...prev, cadence: next } : prev));
  }

  async function handleResetCadence() {
    if (!teamId) return;
    const next = await patchWorkspaceReminderCadence(teamId, null);
    setSettings((prev) => (prev ? { ...prev, cadence: next } : prev));
  }

  async function handleSaveInChannel(mode: InChannelRemindersMode) {
    if (!teamId) return;
    const next = await patchWorkspaceInChannelReminders(teamId, mode);
    setSettings((prev) => (prev ? { ...prev, inChannelReminders: next } : prev));
  }

  async function handleResetInChannel() {
    if (!teamId) return;
    const next = await patchWorkspaceInChannelReminders(teamId, null);
    setSettings((prev) => (prev ? { ...prev, inChannelReminders: next } : prev));
  }

  function handleGrantClick() {
    setGrantValidationError(null);
    setGrantSubmitError(null);
    setGrantSuccessMessage(null);
    const tracks = parseTracksToGrantInput(tracksToGrantInput);
    if (tracks == null) {
      setGrantValidationError('Enter a whole number of tracks greater than zero.');
      return;
    }
    setPendingTracksToGrant(tracks);
    setShowGrantConfirm(true);
  }

  function handleGrantCancel() {
    setShowGrantConfirm(false);
    setPendingTracksToGrant(null);
  }

  async function handleGrantConfirm() {
    if (!teamId || pendingTracksToGrant == null) return;
    setShowGrantConfirm(false);
    setGrantSubmitting(true);
    setGrantSubmitError(null);
    setGrantSuccessMessage(null);
    try {
      await postComplimentaryGrant(teamId, pendingTracksToGrant);
      setGrantSuccessMessage(
        `Granted ${pendingTracksToGrant.toLocaleString()} complimentary tracks.`
      );
      setTracksToGrantInput('');
      setPendingTracksToGrant(null);
      await loadDetail({ silent: true });
    } catch (e) {
      setGrantSubmitError(e instanceof Error ? e.message : 'Failed to grant complimentary tracks');
    } finally {
      setGrantSubmitting(false);
    }
  }

  if (!teamId) {
    return (
      <div style={styles.layout}>
        <p style={styles.error}>Missing team ID.</p>
        <Link to="/workspaces">Back to list</Link>
      </div>
    );
  }

  if (loading) return <div style={styles.layout}>Loading…</div>;
  if (error) {
    return (
      <div style={styles.layout}>
        <p style={styles.error}>{error}</p>
        <Link to="/workspaces">Back to list</Link>
      </div>
    );
  }
  if (!data) return null;

  const { identity, payments, usage, billing } = data;
  const workspaceLabel = identity.teamName || identity.teamDomain || identity.teamId;

  return (
    <div style={styles.layout}>
      <header style={styles.header}>
        <Link to="/workspaces" style={styles.backLink}>
          ← Workspaces
        </Link>
        <h1 style={styles.title}>{workspaceLabel}</h1>
        <code style={styles.teamId}>{identity.teamId}</code>
      </header>

      <div style={styles.tabList} role="tablist" aria-label="Workspace sections">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'overview'}
          onClick={() => selectTab('overview')}
          style={{ ...styles.tab, ...(tab === 'overview' ? styles.tabActive : {}) }}
          data-testid="workspace-tab-overview"
        >
          Overview
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'settings'}
          onClick={() => selectTab('settings')}
          style={{ ...styles.tab, ...(tab === 'settings' ? styles.tabActive : {}) }}
          data-testid="workspace-tab-settings"
        >
          Settings
        </button>
      </div>

      {tab === 'settings' ? (
        <div data-testid="workspace-settings-panel">
          {settingsLoading && <p>Loading…</p>}
          {settingsError && (
            <div style={styles.section} data-testid="workspace-settings-error">
              <p style={styles.error}>{settingsError}</p>
              <button
                type="button"
                onClick={() => void loadSettings()}
                style={styles.confirmCancelButton}
              >
                Retry
              </button>
            </div>
          )}
          {settings && (
            <section style={styles.section}>
              <h2 style={styles.sectionTitle}>Workspace settings</h2>
              <CadenceDefaultRow
                setting={settings.cadence}
                canReset={settings.cadence.layer === 'workspace'}
                onSave={handleSaveCadence}
                onReset={handleResetCadence}
              />
              <InChannelDefaultRow
                setting={settings.inChannelReminders}
                canReset={settings.inChannelReminders.layer === 'workspace'}
                onSave={handleSaveInChannel}
                onReset={handleResetInChannel}
              />
              <section style={{ marginTop: 8 }} data-testid="workspace-broadcast-section">
                <h3 style={styles.h3}>Broadcast mention tracking</h3>
                <BroadcastMentionTrackingToggle
                  teamId={identity.teamId}
                  initialEnabled={settings.broadcastMentionTracking.enabled}
                />
              </section>
            </section>
          )}
        </div>
      ) : (
        <>
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Identity &amp; setup</h2>
            <dl style={styles.dl}>
              <dt>Team ID</dt>
              <dd>{identity.teamId}</dd>
              <dt>Team name</dt>
              <dd>{identity.teamName ?? '—'}</dd>
              <dt>Slack workspace</dt>
              <dd>{identity.teamDomain ?? '—'}</dd>
              <dt>Install date</dt>
              <dd>{identity.installDate ?? '—'}</dd>
              <dt>Installing user</dt>
              <dd>{formatInstallingUser(identity)}</dd>
              <dt>Status</dt>
              <dd>{identity.status ?? '—'}</dd>
              <dt>Source</dt>
              <dd>{identity.source ?? '—'}</dd>
              <dt>Initial tracks granted</dt>
              <dd>{identity.initialTracksGranted ?? '—'}</dd>
              <dt>Billing admins</dt>
              <dd>
                {identity.billingAdminIds?.length ? identity.billingAdminIds.join(', ') : '—'}
              </dd>
            </dl>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Payments</h2>
            <dl style={styles.dl}>
              <dt>Stripe customer ID</dt>
              <dd>{payments.stripeCustomerId ?? '—'}</dd>
              <dt>Plan</dt>
              <dd>{payments.plan ?? '—'}</dd>
              <dt>Subscription status</dt>
              <dd>{payments.subscriptionStatus ?? '—'}</dd>
            </dl>
            {payments.openInStripeUrl && (
              <p style={styles.linkWrap}>
                <a
                  href={payments.openInStripeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.stripeLink}
                >
                  Open in Stripe
                </a>
              </p>
            )}
            {payments.paymentHistory && payments.paymentHistory.length > 0 && (
              <>
                <h3 style={styles.h3}>Payment history</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table data-testid="client-payment-history-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Pack</th>
                        <th>Tracks</th>
                        <th>Purchased by</th>
                        <th>Amount</th>
                        <th>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.paymentHistory.map((p, i) => (
                        <tr key={p.paymentId ?? i}>
                          <td>{formatPaymentDate(p)}</td>
                          <td>{formatPaymentPack(p)}</td>
                          <td>{p.tracks != null ? p.tracks.toLocaleString() : '—'}</td>
                          <td>{formatPurchasedBy(p)}</td>
                          <td>{formatUsdFromCents(p.amount)}</td>
                          <td>{paymentHistoryTypeLabel(p)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Billing</h2>
            <dl style={styles.dl}>
              <dt>Track balance (spendable)</dt>
              <dd>
                {billing?.tracksAvailable ?? 0}{' '}
                <span style={styles.balanceHint}>tracks available to start new tracks</span>
              </dd>
            </dl>
            <div style={styles.grantForm} data-testid="complimentary-grant-section">
              <label htmlFor="tracks-to-grant" style={styles.grantLabel}>
                Tracks to grant
              </label>
              <div style={styles.grantControls}>
                <input
                  id="tracks-to-grant"
                  type="number"
                  min={1}
                  step={1}
                  value={tracksToGrantInput}
                  onChange={(e) => {
                    setTracksToGrantInput(e.target.value);
                    setGrantValidationError(null);
                    setGrantSubmitError(null);
                    setGrantSuccessMessage(null);
                  }}
                  disabled={grantSubmitting}
                  style={styles.grantInput}
                  data-testid="tracks-to-grant-input"
                />
                <button
                  type="button"
                  onClick={handleGrantClick}
                  disabled={grantSubmitting}
                  style={styles.grantButton}
                  data-testid="grant-complimentary-tracks-button"
                >
                  Grant complimentary tracks
                </button>
              </div>
              {grantValidationError && (
                <p style={styles.error} data-testid="grant-validation-error">
                  {grantValidationError}
                </p>
              )}
              {grantSubmitError && (
                <p style={styles.error} data-testid="grant-submit-error">
                  {grantSubmitError}
                </p>
              )}
              {grantSuccessMessage && (
                <p style={styles.grantSuccess} data-testid="grant-success-message">
                  {grantSuccessMessage}
                </p>
              )}
            </div>
            {showGrantConfirm && pendingTracksToGrant != null && (
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="grant-confirm-title"
                style={styles.confirmDialog}
                data-testid="grant-confirm-dialog"
              >
                <h3 id="grant-confirm-title" style={styles.confirmTitle}>
                  Confirm complimentary grant
                </h3>
                <p style={styles.confirmBody}>
                  Grant {pendingTracksToGrant.toLocaleString()} complimentary tracks to{' '}
                  <strong>{workspaceLabel}</strong>?
                </p>
                <div style={styles.confirmActions}>
                  <button
                    type="button"
                    onClick={handleGrantCancel}
                    style={styles.confirmCancelButton}
                    data-testid="grant-confirm-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleGrantConfirm()}
                    style={styles.confirmSubmitButton}
                    data-testid="grant-confirm-submit"
                  >
                    Confirm grant
                  </button>
                </div>
              </div>
            )}
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Usage</h2>
            <dl style={styles.dl}>
              <dt>Tracks used</dt>
              <dd>{usage.tracksUsed ?? '—'}</dd>
              <dt>Tracks granted</dt>
              <dd>{usage.tracksGranted ?? '—'}</dd>
            </dl>
            {usage.byMonth && usage.byMonth.length > 0 && (
              <>
                <h3 style={styles.h3}>By month</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Used</th>
                      <th>Granted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usage.byMonth.map((m, i) => (
                      <tr key={i}>
                        <td>{m.month}</td>
                        <td>{m.used ?? '—'}</td>
                        <td>{m.granted ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function formatPaymentDate(p: ClientPaymentHistoryItem): string {
  if (p.date) {
    const parsed = new Date(p.date);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString();
    }
    return p.date;
  }
  if (typeof p.createdAt === 'number') {
    return formatUnixDate(p.createdAt);
  }
  return '—';
}

export function formatPaymentPack(p: ClientPaymentHistoryItem): string {
  if (isInstallGrantRow(p)) {
    return p.pack?.trim() || p.description?.trim() || PUBLIC_INSTALL_GRANT_PACK_LABEL;
  }
  if (isComplimentaryGrantRow(p)) {
    return COMPLIMENTARY_GRANT_PACK_LABEL;
  }
  return p.pack ?? p.description ?? p.reference ?? '—';
}

export function formatPurchasedBy(p: ClientPaymentHistoryItem): React.ReactNode {
  if (isComplimentaryGrantRow(p)) {
    const email = p.grantedByEmail?.trim();
    if (email) {
      return (
        <span>
          Granted by
          <br />
          <span style={styles.purchaserEmail}>{email}</span>
        </span>
      );
    }
    return '—';
  }

  const name = p.purchasedByDisplayName?.trim();
  const email = p.purchasedByEmail?.trim();
  const userId = p.purchasedByUserId?.trim();
  if (name && email) {
    return (
      <span>
        {name}
        <br />
        <span style={styles.purchaserEmail}>{email}</span>
      </span>
    );
  }
  if (name) return name;
  if (email) return email;
  if (userId) return userId;
  return '—';
}

export function formatInstallingUser(
  identity: Pick<
    ClientIdentity,
    'installedByDisplayName' | 'installedByEmail' | 'installedByUserId'
  >
): React.ReactNode {
  const name = identity.installedByDisplayName?.trim();
  const email = identity.installedByEmail?.trim();
  const userId = identity.installedByUserId?.trim();

  if (name && userId) {
    return (
      <span>
        {name} ({userId})
        {email ? (
          <>
            <br />
            <span style={styles.purchaserEmail}>{email}</span>
          </>
        ) : null}
      </span>
    );
  }
  if (name && email) {
    return (
      <span>
        {name}
        <br />
        <span style={styles.purchaserEmail}>{email}</span>
      </span>
    );
  }
  if (name) return name;
  if (userId) return userId;
  return '—';
}

const styles: Record<string, React.CSSProperties> = {
  layout: {
    maxWidth: 900,
    margin: '0 auto',
    padding: 24,
  },
  header: {
    marginBottom: 24,
  },
  tabList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 24,
  },
  tab: {
    fontSize: 14,
    padding: '8px 14px',
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
  },
  tabActive: {
    background: '#eff6ff',
    borderColor: '#93c5fd',
    color: '#1d4ed8',
    fontWeight: 600,
  },
  backLink: {
    display: 'inline-block',
    marginBottom: 8,
    color: '#2563eb',
    fontSize: 14,
  },
  title: {
    margin: '0 0 4px',
    fontSize: 24,
    fontWeight: 600,
    color: '#1a1a1a',
  },
  teamId: {
    fontSize: 14,
    color: '#6b7280',
    background: '#f3f4f6',
    padding: '2px 8px',
    borderRadius: 4,
  },
  section: {
    background: '#fff',
    padding: 24,
    borderRadius: 8,
    marginBottom: 24,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  sectionTitle: {
    margin: '0 0 16px',
    fontSize: 18,
    fontWeight: 600,
    color: '#374151',
  },
  dl: {
    display: 'grid',
    gridTemplateColumns: '160px 1fr',
    gap: '8px 24px',
    margin: 0,
  },
  dt: {
    color: '#6b7280',
    fontSize: 14,
  },
  dd: {
    margin: 0,
  },
  balanceHint: {
    color: '#6b7280',
    fontSize: 14,
  },
  h3: {
    margin: '20px 0 8px',
    fontSize: 14,
    fontWeight: 600,
    color: '#6b7280',
  },
  linkWrap: {
    marginTop: 12,
  },
  stripeLink: {
    color: '#2563eb',
    fontWeight: 500,
  },
  purchaserEmail: {
    fontSize: 12,
    color: '#6b7280',
  },
  error: {
    color: '#b91c1c',
    marginBottom: 16,
  },
  grantForm: {
    marginTop: 20,
  },
  grantLabel: {
    display: 'block',
    marginBottom: 8,
    fontSize: 14,
    fontWeight: 500,
    color: '#374151',
  },
  grantControls: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'center',
  },
  grantInput: {
    width: 120,
    padding: '8px 10px',
    fontSize: 14,
    border: '1px solid #d1d5db',
    borderRadius: 6,
  },
  grantButton: {
    padding: '8px 14px',
    fontSize: 14,
    fontWeight: 500,
    color: '#fff',
    background: '#2563eb',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
  grantSuccess: {
    marginTop: 12,
    marginBottom: 0,
    color: '#047857',
    fontSize: 14,
  },
  confirmDialog: {
    marginTop: 16,
    padding: 16,
    border: '1px solid #d1d5db',
    borderRadius: 8,
    background: '#f9fafb',
  },
  confirmTitle: {
    margin: '0 0 8px',
    fontSize: 16,
    fontWeight: 600,
    color: '#374151',
  },
  confirmBody: {
    margin: '0 0 16px',
    fontSize: 14,
    color: '#4b5563',
  },
  confirmActions: {
    display: 'flex',
    gap: 12,
  },
  confirmCancelButton: {
    padding: '8px 14px',
    fontSize: 14,
    border: '1px solid #d1d5db',
    borderRadius: 6,
    background: '#fff',
    cursor: 'pointer',
  },
  confirmSubmitButton: {
    padding: '8px 14px',
    fontSize: 14,
    fontWeight: 500,
    color: '#fff',
    background: '#2563eb',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
};
